import { Global } from "@emotion/react";
import styled from "@emotion/styled";
import { PublicKey } from "@solana/web3.js";
import { NATIVE_SOL, STEP_MINT, USDC_MINT, USDT_MINT } from "@stepfinance/common";
import { black, darkGreen, darkGrey, lightGreen, StepTextInput } from "@stepfinance/react";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { Dispatch, FC, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";

import { useAccountsContext } from "../../contexts/accounts";
import { useLanguageContext } from "../../contexts/language/language";
import { useSwappableTokens } from "../../hooks";
import { SwapTokenInfoDisplay } from "../SwapAmountInput/SwapTokenInfoDisplay";
import { TokenList } from "../SwapAmountInput/TokenList";
import { TokenSelectModal } from "../SwapAmountInput/TokenSelectModal";

export type UserBalancesMap = {
  [mint: string]: NormalizedTokenAccount;
};

export type SwapTokenSelectModalProps = {
  mint: PublicKey | string;
  dependentMint?: PublicKey;
  onMintChange?: (mint: PublicKey) => void;
  currencySelectionVisible: boolean;
  setCurrencySelectionVisible: Dispatch<SetStateAction<boolean>>;
};

const commonCurrencies = [
  { mint: USDC_MINT, symbol: "USDC" },
  { mint: STEP_MINT, symbol: "STEP" },
  { mint: NATIVE_SOL, symbol: "SOL" },
  { mint: USDT_MINT, symbol: "USDT" },
];

export const SwapTokenSelectModal: FC<SwapTokenSelectModalProps> = ({
  dependentMint,
  mint,
  onMintChange,
  currencySelectionVisible,
  setCurrencySelectionVisible,
}) => {
  const [searchValue, setSearchValue] = useState<string>("");

  const { swappableTokens } = useSwappableTokens();
  const { userTokenAccounts } = useAccountsContext();

  const {
    currentLanguageData: {
      pages: {
        swapLP: { tokenSelectModal: modalText },
      },
    },
  } = useLanguageContext();

  const userBalancesMap = useMemo(() => {
    const map: UserBalancesMap = {};
    for (const account of userTokenAccounts) {
      map[account.info.mint] = account;
    }
    return map;
  }, [userTokenAccounts]);

  const onTokenSelectModalClosed = useCallback(() => {
    setCurrencySelectionVisible(false);
    setSearchValue("");
  }, [setCurrencySelectionVisible, setSearchValue]);

  const onMintSelected = useCallback(
    (mint: string) => {
      onMintChange?.(new PublicKey(mint));
      onTokenSelectModalClosed();
    },
    [onMintChange, onTokenSelectModalClosed],
  );

  return (
    <>
      <Global
        styles={`
        .token-select-modal-wrap {
          position: absolute;
        }
      `}
      />
      <TokenSelectModal
        title={modalText.selectAToken}
        onClose={onTokenSelectModalClosed}
        width={400}
        visible={currencySelectionVisible}
      >
        <>
          <SearchInputContainer>
            <StepTextInput
              placeholder={modalText.searchByNameOrSymbol}
              value={searchValue}
              variant="filled"
              borderless
              style={{ backgroundColor: "black" }}
              inputSize="medium"
              onChange={(e) => setSearchValue(e.target.value.toLowerCase())}
            />
          </SearchInputContainer>
          <>
            <CommonBaseCurrencies>
              <span>{modalText.commonBaseAssets}</span>
            </CommonBaseCurrencies>
            <CommonCurrencyTagContainer>
              {commonCurrencies.map(({ mint: commonCurrencyMint, symbol }) => {
                const tokenDisabled = dependentMint ? commonCurrencyMint.equals(dependentMint) : false;
                return (
                  <CommonCurrencyTag
                    key={commonCurrencyMint.toBase58()}
                    selected={
                      mint instanceof PublicKey
                        ? commonCurrencyMint.equals(mint)
                        : commonCurrencyMint.toBase58() === mint
                    }
                    mint={commonCurrencyMint}
                    symbol={symbol}
                    onClick={() => {
                      if (!tokenDisabled) {
                        onMintSelected(commonCurrencyMint.toBase58());
                      }
                    }}
                    disabled={tokenDisabled}
                  />
                );
              })}
            </CommonCurrencyTagContainer>
          </>
          <TokenList
            disabledMintFromTokenList={dependentMint}
            mint={mint}
            onMintSelected={onMintSelected}
            searchValue={searchValue}
            swappableTokens={swappableTokens}
            userBalancesMap={userBalancesMap}
          />
        </>
      </TokenSelectModal>
    </>
  );
};

const SearchInputContainer = styled.div`
  margin-bottom: 10px;
`;

const CommonBaseCurrencies = styled.div`
  font-size: 10px;
  line-height: 22px;
  color: #b2b2b2;
  margin-bottom: 10px;
`;

const CommonCurrencyTagContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const CommonCurrencyTag = styled(SwapTokenInfoDisplay)<{
  selected: boolean;
  disabled: boolean;
}>`
  padding: 3px 7px;
  height: 26px;
  background-color: ${({ selected, disabled }) => (disabled ? darkGrey : selected ? lightGreen : darkGreen)};
  border: ${({ disabled }) => (disabled ? undefined : `1px solid ${darkGreen}`)};
  box-sizing: border-box;
  border-radius: 2px;
  margin-right: 5px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  span {
    color: ${({ selected, disabled }) => (disabled ? "#434343" : selected ? black : lightGreen)};
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
  }

  :hover {
    border: ${({ selected, disabled }) => (selected || disabled ? undefined : "1px solid #06d6a0")};
  }
`;
